import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFarm, farmInitials } from "@/lib/farmContext";
import { TEMPLATES, RESERVED_TOP, todayISO } from "@/lib/actionTemplates";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScanCTA } from "@/components/ScanCTA";
import { ToggleField } from "@/components/actions/ToggleField";
import { ChipListField } from "@/components/actions/ChipListField";
import { ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { queueActionOutbox } from "@/lib/sync";
import { uuid, getOrCreateDeviceId } from "@/lib/db";
import { findAnimalByChipLocal, findAnimalByLomoLocal, resyncAnimalLocal, useLots, listAnimalsInLotLocal } from "@/lib/repo";

const NOTE_TONE = {
  alarm: "bg-[hsl(var(--state-unknown-bg))] text-[hsl(var(--state-unknown))] border-[hsl(var(--state-unknown))]",
  warn: "bg-[hsl(var(--state-missing-bg))] text-[hsl(var(--state-missing))] border-[hsl(var(--state-missing))]",
  info: "bg-[hsl(var(--state-present-bg))] text-[hsl(var(--state-present))] border-[hsl(var(--state-present))]",
  owner: "bg-[hsl(var(--c-evento)/0.12)] text-[hsl(var(--c-evento))] border-[hsl(var(--c-evento))]",
};

// Fix B (2026-08-24 · 2ª pasada): el override local ganaba sobre el text-base del
// Input base (tailwind-merge). 'text-base sm:text-[15px]' asegura 16px en móvil.
const INPUT_CLS = "h-12 text-base sm:text-[15px] focus:placeholder-transparent";

export default function ActionRunner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { farms, activeFarm, activeFarmId, loading: farmLoading } = useFarm();
  const template = TEMPLATES[id];

  const [values, setValues] = useState({});
  const [lookup, setLookup] = useState({});
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("single");

  const set = useCallback((name, val) => setValues((v) => ({ ...v, [name]: val })), []);

  const hasBulk = !!template?.bulkFields;
  const activeFields = mode === "bulk" && hasBulk ? template.bulkFields : template?.fields;
  const activeActionType = mode === "bulk" && hasBulk ? template.bulkActionType : template?.actionType;

  const switchMode = useCallback((m) => { setMode(m); setValues({}); setLookup({}); }, []);

  // Fix Q (2026-10-21): selector de lote (Cambio de lote). Al elegir el lote de ORIGEN
  // se suman a la lista todos sus animales activos (sin repetir los ya cargados).
  const lots = useLots(values.farm_id || activeFarmId);
  const pickLot = useCallback(async (f, v) => {
    set(f.name, v);
    if (f.k !== "fromlot" || !f.fills || !v) return;
    const inLot = (await listAnimalsInLotLocal(v)).filter((a) => a.chip_code);
    setValues((cur) => {
      const list = Array.isArray(cur[f.fills]) ? cur[f.fills] : [];
      const have = new Set(list.map((r) => r.chip_code));
      return { ...cur, [f.fills]: [...list, ...inLot.filter((a) => !have.has(a.chip_code)).map((a) => ({ chip_code: a.chip_code }))] };
    });
    toast.message(`${inLot.length} animal(es) del lote cargados`);
  }, [set]);

  useEffect(() => {
    if (!template) { navigate("/acciones", { replace: true }); return; }
    if (template.navTo) { navigate(template.navTo, { replace: true }); return; }
    if (user?.role === "vet" && template.actionType !== "palpation") {
      toast.error("El veterinario solo puede registrar palpaciones");
      navigate("/acciones/11", { replace: true });
      return;
    }
    if (template.ownerOnly && user?.role === "handler") {
      toast.error("Solo el dueño puede usar esta plantilla");
      navigate("/acciones", { replace: true });
    }
  }, [template, user, navigate]);

  useEffect(() => { setMode("single"); }, [id]);

  useEffect(() => {
    if (!template?.fields) return;
    const init = {};
    template.fields.forEach((f) => { if (f.def === "today") init[f.name] = todayISO(); });
    setValues((v) => ({ ...init, ...v }));
  }, [template]);

  useEffect(() => {
    if (!template?.fields) return;
    const placeField = template.fields.find((f) => f.k === "place");
    if (!placeField) return;
    if (activeFarmId) set(placeField.name, activeFarmId);
    else if (farms.length === 1) set(placeField.name, farms[0].id);
  }, [activeFarmId, farms, template, set]);

  useEffect(() => {
    if (!template || template.navTo || farmLoading) return;
    const hasPlace = template.fields?.some((f) => f.k === "place");
    if (hasPlace && !activeFarmId && farms.length > 1) {
      toast.message("Elegí una finca antes de registrar la acción");
      navigate("/acciones", { replace: true });
    }
  }, [template, activeFarmId, farms, farmLoading, navigate]);

  const doLookup = useCallback(async (field, chip) => {
    const code = (chip || "").trim();
    if (!code) { setLookup((l) => ({ ...l, [field.name]: null })); return; }
    const found = await findAnimalByChipLocal(code);
    if (field.mustBeFree) {
      if (found) setLookup((l) => ({ ...l, [field.name]: { status: "taken", label: `Chip ya vinculado a Lomo ${found.lomo_number || "—"}` } }));
      else setLookup((l) => ({ ...l, [field.name]: { status: "free", label: "Chip disponible" } }));
      return;
    }
    if (found) {
      setLookup((l) => ({ ...l, [field.name]: { status: "found", label: `Lomo ${found.lomo_number || "—"} · ${found.category || "animal"}`, animalId: found.id } }));
      setValues((v) => (v.lomo_number ? v : { ...v, lomo_number: found.lomo_number || "" }));
    } else {
      setLookup((l) => ({ ...l, [field.name]: { status: "missing", label: "No encontré un animal con ese chip" } }));
    }
  }, []);

  const revealedHidden = useMemo(() => {
    const s = new Set();
    activeFields?.forEach((f) => { if (f.k === "q" && f.reveal && values[f.name] === "yes") s.add(f.reveal); });
    return s;
  }, [activeFields, values]);

  if (!template || template.navTo) return null;

  const color = `var(--c-${template.cat})`;

  const renderField = (f, key) => {
    switch (f.k) {
      case "date":
        return (
          <Field key={key} f={f}>
            <Input data-testid={`field-${f.name}`} type="date" value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className={INPUT_CLS} />
          </Field>
        );
      case "text":
        return (
          <Field key={key} f={f}>
            <Input data-testid={`field-${f.name}`} value={values[f.name] || ""}
              onChange={(e) => set(f.name, e.target.value)}
              onBlur={() => (f.lookup || f.mustBeFree ? doLookup(f, values[f.name]) : null)}
              placeholder={f.ph} className={cn(INPUT_CLS, (f.scan || f.lookup) && "font-mono")} />
            {f.scan && (
              <ScanCTA testId={`scan-${f.name}`} label="Escanear chip" fullWidth
                onChip={(c) => { set(f.name, c); if (f.lookup || f.mustBeFree) doLookup(f, c); }} />
            )}
            <LookupHint state={lookup[f.name]} />
            {f.note && <p className="text-[12px] text-muted-foreground flex items-center gap-1">ⓘ {f.note}</p>}
          </Field>
        );
      case "num":
        return (
          <Field key={key} f={f}>
            <div className="relative">
              <Input data-testid={`field-${f.name}`} type="number" inputMode="decimal" value={values[f.name] || ""}
                onChange={(e) => set(f.name, e.target.value)} placeholder={f.ph} className={cn(INPUT_CLS, "pr-14")} />
              {f.unit && <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[12px] font-mono text-muted-foreground pointer-events-none">{f.unit}</span>}
            </div>
          </Field>
        );
      case "area":
        return (
          <Field key={key} f={f}>
            <Textarea data-testid={`field-${f.name}`} value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.ph} rows={3} className="text-base sm:text-[15px] focus:placeholder-transparent" />
          </Field>
        );
      case "place":
        return null; // La finca activa se elige en el hub de Acciones (global)
      case "destfarm":
        return (
          <Field key={key} f={f}>
            <Select value={values[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
              <SelectTrigger className="h-12 text-[15px]" data-testid={`field-${f.name}`}><SelectValue placeholder="Elegí la finca destino" /></SelectTrigger>
              <SelectContent>{farms.map((fa) => <SelectItem key={fa.id} value={fa.id}>{fa.name}</SelectItem>)}</SelectContent>
            </Select>
            {f.note && <p className="text-[12px] text-muted-foreground">ⓘ {f.note}</p>}
          </Field>
        );
      case "originfarm":
        return (
          <Field key={key} f={f}>
            <Select value={values[f.name] || ""} onValueChange={(v) => set(f.name, v === "_none" ? "" : v)}>
              <SelectTrigger className="h-12 text-[15px]" data-testid={`field-${f.name}`}><SelectValue placeholder="Elegí la finca de origen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Sin especificar</SelectItem>
                {farms.map((fa) => <SelectItem key={fa.id} value={fa.id}>{fa.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        );
      case "lot":
      case "fromlot":
        return (
          <Field key={key} f={f}>
            <Select value={values[f.name] || ""} onValueChange={(v) => pickLot(f, v)}>
              <SelectTrigger className="h-12 text-[15px]" data-testid={`field-${f.name}`}>
                <SelectValue placeholder={f.k === "lot" ? "Elegí el lote de destino" : "Elegí el lote de origen"} />
              </SelectTrigger>
              <SelectContent>{lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
            {f.note && <p className="text-[12px] text-muted-foreground">ⓘ {f.note}</p>}
          </Field>
        );
      case "subhead":
        return (
          <div key={key} className="pt-3 border-t border-border">
            <h2 className="font-heading font-bold text-[15px] tracking-tight text-foreground">{f.txt}</h2>
          </div>
        );
      case "note":
        return (
          <div key={key} className={cn("flex gap-3 rounded-[calc(var(--radius)-0.3rem)] border-l-4 px-4 py-3.5 text-[13px] font-medium leading-relaxed", NOTE_TONE[f.tone] || NOTE_TONE.info)}>
            <span role="img" aria-label="nota" className="text-[18px] leading-none shrink-0">{f.ni}</span>
            <span>{f.txt}</span>
          </div>
        );
      case "q":
      case "toggle": {
        const active = f.opts?.find((o) => o.v === values[f.name]);
        return (
          <div key={key} className="flex flex-col gap-3">
            <ToggleField field={f} value={values[f.name]} onChange={set} />
            {f.reveals && active?.reveal && renderField({ ...f.reveals[active.reveal] }, `${key}-rev`)}
          </div>
        );
      }
      case "chips":
        return <ChipListField key={key} field={f} value={values[f.name]} onChange={set} />;
      default:
        return null;
    }
  };

  const validate = () => {
    for (const f of activeFields) {
      if (!f.req || !f.name) continue;
      if (f.hidden && !revealedHidden.has(f.name)) continue;
      const v = values[f.name];
      if (f.k === "chips") { if (!Array.isArray(v) || v.length === 0) { toast.error("Agregá al menos un chip"); return false; } continue; }
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
        toast.error(`Completá: ${f.lab.replace(/[^\wáéíóúñ\s]/gi, "").trim()}`);
        return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    const payload = {};
    const top = { type: activeActionType };
    Object.entries(values).forEach(([k, val]) => {
      if (val === "" || val === undefined || val === null) return;
      if (RESERVED_TOP.includes(k)) top[k] = val;
      else payload[k] = val;
    });
    top.payload = payload;
    // Idempotencia (Tanda 3 — Blindaje de datos): el ID se genera ACÁ, antes
    // del primer intento de red. Si el envío falla sin respuesta (offline,
    // timeout) y termina reintentándose desde el outbox, siempre viaja con
    // el MISMO action_id — el servidor detecta el reintento y nunca aplica
    // la acción dos veces (nunca dos muertes, dos ventas, etc.).
    top.action_id = uuid();
    // Registro de autoría (Funcionalidad veterinario): desde qué equipo se
    // registró la acción — clave en la palpación, donde el veterinario puede
    // trabajar sin señal y sincronizar más tarde.
    top.device_id = await getOrCreateDeviceId();
    // El animal ya se resolvió localmente (por eso se ve en pantalla el match
    // de chip/lomo) — mandamos su id para que el backend resuelva por id y no
    // dependa de que el chip/lomo hayan terminado de sincronizar al server.
    const chipLookup = lookup.chip_code;
    if (chipLookup?.status === "found" && chipLookup.animalId) top.animal_id = chipLookup.animalId;
    setSaving(true);
    try {
      const res = await api.submitAction(top);
      if (template.successTo === "animal" && res.animal) {
        toast.success(`${template.title} registrada`);
        navigate(`/animales/${res.animal.id}`);
      } else if (res.processed_count !== undefined) {
        toast.success(`${template.title}: ${res.processed_count} animal(es) procesados`);
        if (res.not_found?.length) toast.warning(`${res.not_found.length} chip(s) no encontrados`);
        if (res.skipped?.length) toast.warning(`${res.skipped.length} animal(es) no se movieron (de otra finca o ya estaban en ese lote)`);
        navigate("/acciones");
      } else {
        toast.success(`${template.title} registrada`);
        navigate("/acciones");
      }
    } catch (err) {
      if (!err?.response) {
        // Sin respuesta del servidor (offline / error de red): encolamos la
        // acción para reenviarla cuando vuelva la señal. La lógica de negocio
        // se aplica en el backend al reenviar, así que no confirmamos el
        // efecto local — solo avisamos que quedó pendiente.
        await queueActionOutbox(top);
        toast.success(`${template.title} guardada — se sincroniza al volver la señal`);
        navigate("/acciones");
      } else if (err.response.status === 404) {
        // El animal se ve en pantalla (match local) pero el server no lo
        // encontró: una sincronización se perdió alguna vez. En vez de fallar,
        // reparamos — re-subimos el animal completo y encolamos la acción
        // para que se aplique sola en cuanto termine de sincronizar.
        const chipVal = (top.chip_code || "").trim();
        const lomoVal = (top.lomo_number || "").trim();
        const localAnimal = (chipVal ? await findAnimalByChipLocal(chipVal) : null)
          || (lomoVal ? await findAnimalByLomoLocal(lomoVal) : null);
        if (localAnimal) {
          await resyncAnimalLocal(localAnimal.id);
          await queueActionOutbox({ ...top, animal_id: localAnimal.id });
          toast.success("Guardado — terminando de sincronizar");
          if (template.successTo === "animal") navigate(`/animales/${localAnimal.id}`);
          else navigate("/acciones");
        } else {
          toast.error(err?.response?.data?.detail || "No pude registrar la acción");
        }
      } else {
        toast.error(err?.response?.data?.detail || "No pude registrar la acción");
      }
    } finally { setSaving(false); }
  };

  const acceptField = activeFields.find((f) => f.k === "accept");

  return (
    <div className="max-w-xl mx-auto" data-testid={`action-runner-${template.id}`}>
      <button onClick={() => navigate("/acciones")} data-testid="action-back"
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors mb-4">
        <ArrowLeft size={16} /> Acciones
      </button>

      {/* Encabezado de la plantilla (sin márgenes negativos, sin solapamientos) */}
      <div className="rounded-[var(--radius)] border p-6 mb-8 text-center"
        style={{ background: `hsl(${color} / 0.10)`, borderColor: `hsl(${color} / 0.28)` }}>
        <div className="inline-flex h-16 w-auto min-w-[64px] px-3 rounded-[calc(var(--radius)-0.2rem)] items-center justify-center text-[28px] leading-none whitespace-nowrap bg-card border-2 shadow-sm mb-3"
          style={{ borderColor: `hsl(${color} / 0.4)` }}>
          <span role="img" aria-label={template.title}>{template.ico}</span>
        </div>
        <h1 className="font-heading font-bold text-[22px] leading-tight" style={{ color: `hsl(${color})` }}>{template.title}</h1>
        <div className="font-mono text-[11px] tracking-[0.12em] uppercase text-muted-foreground mt-1.5">{template.num} · {template.sub}</div>
        {activeFarm && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border-2 bg-card px-3 py-1.5 shadow-sm"
            style={{ borderColor: `hsl(${color} / 0.32)` }} data-testid="runner-active-farm">
            <span className="grid place-items-center h-6 w-6 rounded-md font-heading font-bold text-[10px] text-white"
              style={{ background: `hsl(${color})` }}>{farmInitials(activeFarm.name)}</span>
            <span className="font-heading font-semibold text-[13px] text-foreground">{activeFarm.name}</span>
          </div>
        )}
      </div>

      {/* Modo individual / masivo (solo plantillas con carga por lista, ej. Palpación) */}
      {hasBulk && (
        <div className="grid grid-cols-2 gap-2.5 mb-7" data-testid="action-mode-toggle">
          <button type="button" onClick={() => switchMode("single")} data-testid="action-mode-single"
            className={cn("h-11 rounded-[calc(var(--radius)-0.3rem)] text-[13px] font-heading font-semibold border-2 transition-colors",
              mode !== "single" && "bg-muted/40 text-muted-foreground border-transparent")}
            style={mode === "single" ? { background: `hsl(${color} / 0.14)`, color: `hsl(${color})`, borderColor: `hsl(${color} / 0.4)` } : undefined}>
            Individual
          </button>
          <button type="button" onClick={() => switchMode("bulk")} data-testid="action-mode-bulk"
            className={cn("h-11 rounded-[calc(var(--radius)-0.3rem)] text-[13px] font-heading font-semibold border-2 transition-colors",
              mode !== "bulk" && "bg-muted/40 text-muted-foreground border-transparent")}
            style={mode === "bulk" ? { background: `hsl(${color} / 0.14)`, color: `hsl(${color})`, borderColor: `hsl(${color} / 0.4)` } : undefined}>
            Carga masiva (lista)
          </button>
        </div>
      )}

      {/* Campos */}
      <div className="flex flex-col gap-6">
        {activeFields.map((f, i) => {
          if (f.k === "accept") return null;
          if (f.hidden && !revealedHidden.has(f.name)) return null;
          return renderField(f, i);
        })}
      </div>

      {/* CTA táctil */}
      <button onClick={submit} disabled={saving} data-testid="action-submit"
        className="w-full min-h-[60px] mt-9 rounded-[var(--radius)] text-white font-heading text-[16px] font-bold border-b-4 border-black/25 active:border-b-0 active:translate-y-[3px] active:mb-[3px] transition-all duration-150 flex items-center justify-center gap-2.5 shadow-md disabled:opacity-60 disabled:active:translate-y-0"
        style={{ background: `hsl(${color})` }}>
        <CheckCircle2 size={20} /> {saving ? "Guardando…" : (acceptField?.lab || "Guardar")}
      </button>
      <div className="h-4" />
    </div>
  );
}

function Field({ f, children }) {
  return (
    <div className="flex flex-col gap-2" data-testid={f.name ? `field-group-${f.name}` : undefined}>
      <Label className="text-[13.5px] font-heading font-semibold text-foreground flex items-center gap-1">
        {f.lab}
        {f.req ? <span className="text-destructive">*</span> : f.opt ? <span className="text-muted-foreground font-normal text-[11px]">(opcional)</span> : null}
      </Label>
      {children}
    </div>
  );
}

function LookupHint({ state }) {
  if (!state) return null;
  if (state.status === "found" || state.status === "free")
    return <p className="text-[12.5px] flex items-center gap-1.5 text-[hsl(var(--state-present))]" data-testid="lookup-found"><CheckCircle2 size={14} />{state.label}</p>;
  if (state.status === "missing" || state.status === "taken")
    return <p className="text-[12.5px] flex items-center gap-1.5 text-[hsl(var(--state-missing))]" data-testid="lookup-warning"><AlertTriangle size={14} />{state.label}</p>;
  return null;
}
